import type { ApiClient, components } from "@cntrl/api-client";
import type { TokenProvider } from "@cntrl/native";
import { passkeyGet, passkeyCreate } from "./passkey.js";
import { isNative, trustedDevice } from "@cntrl/native";
import type { SessionSync } from "./sessionSync.js";

type AuthTokenResponse = components["schemas"]["AuthTokenResponse"];
type MeDocument = components["schemas"]["MeDocument"];
type NavigationDocument = components["schemas"]["NavigationDocument"];

/**
 * A login attempt's outcome, discriminated for the two-step flow: a completed session (OTP off, an
 * exempt address, or the passkey path server-side), or the emailed-OTP challenge that only
 * {@link verifyOtp} can complete. Wrong credentials still THROW ({@link AuthError}) — the union is only
 * for the two success shapes of POST /auth/login.
 */
export type LoginResult =
  | { kind: "session"; session: AuthTokenResponse }
  | { kind: "otp"; loginToken: string; email: string };

/**
 * Boot the session. Native: load the Keychain bearer. Web: call POST /auth/reissue — the
 * httpOnly Path-scoped reissue cookie (T-08) rides automatically (credentials: include), and on 200 we hold
 * the fresh bearer in memory. Returns whether we are authenticated. Never throws on a "no session" (401/
 * network) — a fresh visitor simply lands on /login.
 */
export async function bootstrapSession(
  api: ApiClient,
  tokenProvider: TokenProvider,
  sync?: SessionSync,
): Promise<boolean> {
  if (isNative()) {
    await tokenProvider.load();
    return tokenProvider.isAuthenticated();
  }
  // Through the cross-tab lock + broadcast when available: this tab's reissue rotates the bearer every OTHER
  // open tab is holding, so they must be handed the new one (sessionSync.ts).
  if (sync) {
    await sync.reissue();
    return tokenProvider.isAuthenticated();
  }
  try {
    const { data, response } = await api.POST("/api/v1/auth/reissue", {});
    if (response.ok && data?.token) {
      await tokenProvider.setBearer(data.token);
      return true;
    }
  } catch {
    // Network/CORS error at boot is NOT a session failure to surface — treat as unauthenticated, keep any
    // bearer we already hold (we never clear on non-401).
  }
  return tokenProvider.isAuthenticated();
}

/**
 * Log in. Resolves to a {@link LoginResult}: on the session shape the bearer is stored through the
 * provider (the server sets the reissue cookie); on the challenge shape nothing is stored — the caller
 * advances to the code step and completes via {@link verifyOtp}. The 200 union is honest in the
 * generated schema (oneOf), narrowed here on the discriminating fields.
 */
export async function login(
  api: ApiClient,
  tokenProvider: TokenProvider,
  input: { email: string; password: string },
): Promise<LoginResult> {
  const client = isNative() ? "ios" : "web";
  // The app's trusted-device key for this address (ART-317), from the Keychain; the web's rides its cookie.
  const trustedDeviceToken = await trustedDevice.get(input.email);
  const { data, error, response } = await api.POST("/api/v1/auth/login", {
    body: { email: input.email, password: input.password, client, ...(trustedDeviceToken ? { trustedDeviceToken } : {}) },
  });
  if (!response.ok || !data) throw new AuthError(response.status, error);

  if ("token" in data && data.token) {
    await tokenProvider.setBearer(data.token);
    return { kind: "session", session: data };
  }
  if ("otpRequired" in data && data.otpRequired && data.loginToken) {
    return { kind: "otp", loginToken: data.loginToken, email: data.email ?? input.email };
  }
  // A 200 matching neither shape is a contract break, not a user error — fail loudly.
  throw new AuthError(response.status, error);
}

/**
 * Complete a two-step login: the emailed 6-digit code + the challenge's loginToken in, a stored session
 * out. Wrong/expired/burned code ⇒ 401 (the code burns after the attempt cap server-side); too many
 * tries ⇒ 429 — both surface as {@link AuthError} for the code step to render.
 */
export async function verifyOtp(
  api: ApiClient,
  tokenProvider: TokenProvider,
  input: { loginToken: string; code: string; trustDevice?: boolean; email?: string },
): Promise<AuthTokenResponse> {
  const { data, error, response } = await api.POST("/api/v1/auth/verify-otp", {
    body: { loginToken: input.loginToken, code: input.code, ...(input.trustDevice ? { trustDevice: true } : {}) },
  });
  if (!response.ok || !data?.token) throw new AuthError(response.status, error);
  await tokenProvider.setBearer(data.token);
  // "Trust this device" (ART-317): the app keeps its key for the next sign-in. The web gets none in the body —
  // the server set it as an httpOnly cookie.
  if (data.trustedDeviceToken && input.email) await trustedDevice.set(input.email, data.trustedDeviceToken);
  return data;
}

/**
 * Create an account from an invite link and accept the invite, in one step (ART-239) — the `/join` page's
 * submit. The account is made for the address the invite names, so there is no email field. On success the
 * bearer is stored exactly as a completed login stores it. 400 = the link is no longer valid; 409 = that address
 * already has an account (sign in, then accept).
 */
export async function joinByInvite(
  api: ApiClient,
  tokenProvider: TokenProvider,
  input: { token: string; firstName: string; lastName: string; password: string },
): Promise<AuthTokenResponse> {
  const client = isNative() ? "ios" : "web";
  const { data, error, response } = await api.POST("/api/v1/auth/join", {
    body: { ...input, client },
  });
  if (!response.ok || !data?.token) throw new AuthError(response.status, error);
  await tokenProvider.setBearer(data.token);
  return data;
}

/** Log out: revoke server-side, then purge the provider (memory + Keychain).*/
export async function logout(api: ApiClient, tokenProvider: TokenProvider): Promise<void> {
  try {
    await api.POST("/api/v1/auth/logout", {});
  } catch {
    // A network/5xx on the server revoke must NOT abort the local logout — logout is user intent, not a
    // 401 (openapi-fetch re-throws a fetch failure; without this catch the rejection would skip the
    // caller's cache-scorch + redirect, stranding the prior principal's cache on-screen). The server
    // session lapses on its own TTL; we always clear locally.
  } finally {
    await tokenProvider.clear();
  }
}

/** Request a reset link. Always 202 server-side (no account enumeration).*/
export async function forgotPassword(api: ApiClient, email: string): Promise<void> {
  await api.POST("/api/v1/auth/forgot-password", { body: { email } });
}

/** Complete a reset by link. Single-use; revokes all sessions server-side.*/
export async function resetPassword(
  api: ApiClient,
  input: { token: string; newPassword: string },
): Promise<void> {
  const { error, response } = await api.POST("/api/v1/auth/reset-password", {
    body: { token: input.token, newPassword: input.newPassword },
  });
  if (!response.ok) throw new AuthError(response.status, error);
}

export async function fetchMe(api: ApiClient): Promise<MeDocument> {
  const { data, error, response } = await api.GET("/api/v1/me");
  if (!response.ok || !data) throw new AuthError(response.status, error);
  return data;
}

/**
 * May this account hold a conversation at all — the SERVER's answer (`core.has_inbox`), read off `/me`.
 *
 * False for the artist/owner account (it IS the band, not a correspondent) and for LABEL and BOOKING seats;
 * true for crew and for MANAGEMENT (PO, 2026-09-11).
 *
 * **Read, never derived.** A seat carries `root`, so "an org seat whose root is management" looks like the
 * same answer — but the server keys on `organizations.org_type`. Two columns for one question is how a
 * displayed rule and an enforced one come apart, and they agree today only by construction.
 *
 * **It lives here so the DOOR and the ROUTE cannot disagree.** The shell hid the Messages icon from the band
 * account since 2026-08-15 and `/messages` still opened on a deep link — because a hidden button is not a
 * gate. One predicate, both callers.
 */
export function hasInbox(me: MeDocument | undefined): boolean {
  return me?.hasInbox === true;
}

/**
 * May this account START a group chat (ART-159)? The artist tier and the tour manager — NARROWER than
 * {@link hasInbox}, which production crew and management also pass. Served by the same document, from
 * `core.can_create_group_thread`, and never derived from a seat here: the create route refuses
 * independently, and the handoff records that legacy's version of this gate was client-only.
 */
export function canCreateGroupChat(me: MeDocument | undefined): boolean {
  return me?.canCreateGroupChat === true;
}

export async function fetchNavigation(api: ApiClient, context?: string): Promise<NavigationDocument> {
  const { data, error, response } = await api.GET("/api/v1/me/navigation", {
    params: context ? { query: { context } } : undefined,
  });
  if (!response.ok || !data) throw new AuthError(response.status, error);
  return data;
}

/** Surfaces the server's error verbatim (a mutation never reports success on an unpersisted write).*/
/**
 * Sign in with a platform passkey (Touch ID / Face ID). Two hops: ask for a challenge, drive the
 * platform prompt, hand the assertion back. A passkey NEVER runs the emailed OTP step — the server
 * mints the session directly — so this returns the same shape a completed OTP does.
 *
 * `email` is OPTIONAL and passing it only scopes `allowCredentials`. Omitted, the server answers a
 * discoverable ("usernameless") challenge and the authenticator offers whatever it holds. We never ask
 * whether an address HAS a passkey: that question is an enumeration oracle, and the server deliberately
 * answers a well-formed challenge either way.
 */
export async function loginWithPasskey(
  api: ApiClient,
  tokenProvider: TokenProvider,
  input: { email?: string } = {},
): Promise<AuthTokenResponse> {
  const email = input.email?.trim();
  const begin = await api.POST("/api/v1/auth/passkey/auth/options", {
    body: email ? { email } : {},
  });
  if (!begin.response.ok || !begin.data) throw new AuthError(begin.response.status, begin.error);

  // No cast. Both options routes now declare `PasskeyOptionsResponse`, so `begin.data` IS `{ challengeId,
  // publicKey }` — the CONTROLLER's shape, checked by the compiler rather than asserted here. The cast this
  // replaces is what let TASK-062 read `optionsJson`, a field that only ever existed on the internal service
  // record: it type-checked against nothing, and `JSON.parse(undefined)` killed every passkey call.
  const { challengeId, publicKey } = begin.data;
  const credential = await passkeyGet(publicKey);   // throws NotAllowedError if the user cancels

  const client = isNative() ? "ios" : "web";
  // NOT `as never`. That cast is what let the credential ship without `clientExtensionResults` — a field
  // the generated schema marks REQUIRED, so the untyped body compiled and every sign-in 400'd before
  // verification (2026-09-06; the same family as the `optionsJson` bug two lines up). Typed, the contract
  // is a compile error.
  const { data, error, response } = await api.POST("/api/v1/auth/passkey/auth", {
    body: { challengeId, credential, client },
  });
  if (!response.ok || !data?.token) throw new AuthError(response.status, error);
  await tokenProvider.setBearer(data.token);
  return data;
}

/**
 * Confirm an email address from its single-use link. `[AllowPublic]` server-side, so it works signed OUT —
 * which is the ordinary case, since the link is opened from a mail client on whatever device is to hand.
 *
 * Deliberately NOT called on page load. The token is single-use, and mail scanners and link-preview bots
 * follow URLs in email before a person ever does — a GET that consumed it would let a scanner burn the
 * verification and leave the user with a dead link. The screen renders a button; this runs on the press.
 * That is the same shape `/reset` already uses, for the same reason.
 */
export async function verifyEmail(api: ApiClient, token: string): Promise<void> {
  const { error, response } = await api.POST("/api/v1/auth/verify-email", { body: { token } });
  if (!response.ok) throw new AuthError(response.status, error);
}

/**
 * Does the CALLER already hold a passkey? Self-scoped — the server reads it off the resolved principal and
 * this sends no address, so it is not the enumeration oracle the legacy `webauthn/check?email=` was.
 *
 * Answers FALSE on any failure rather than throwing. It gates an OFFER, so the two ways of being wrong are
 * not symmetric: a false negative shows the card to someone who has a passkey (they decline, or the
 * `InvalidStateError` backstop catches it), while letting the error escape would break the login handoff for
 * a question that is only ever advisory.
 */
export async function hasPasskey(api: ApiClient): Promise<boolean> {
  try {
    const { data, response } = await api.GET("/api/v1/auth/passkeys", {});
    return response.ok && Array.isArray(data) && data.length > 0;
  } catch {
    return false;
  }
}

/**
 * Enrol a platform passkey for the CALLER (both routes are authenticated), so the next sign-in can use
 * Touch ID. Offered right after a password login, exactly where the specimen offers it.
 */
export async function enrolPasskey(api: ApiClient): Promise<void> {
  const begin = await api.POST("/api/v1/auth/passkey/register/options", {});
  if (!begin.response.ok || !begin.data) throw new AuthError(begin.response.status, begin.error);

  const { challengeId, publicKey } = begin.data;   // typed — see the note in `loginWithPasskey`
  const credential = await passkeyCreate(publicKey);   // throws NotAllowedError if the user cancels

  // Typed, deliberately — see the note in `loginWithPasskey`. Enrolment additionally requires
  // `response.transports`, which the same cast was hiding.
  const { error, response } = await api.POST("/api/v1/auth/passkey/register", {
    body: { challengeId, credential },
  });
  if (!response.ok) throw new AuthError(response.status, error);
}

export class AuthError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem?: unknown,
  ) {
    super(problemDetail(problem) ?? `Request failed (${status})`);
    this.name = "AuthError";
  }
}

function problemDetail(problem: unknown): string | undefined {
  if (problem && typeof problem === "object") {
    const p = problem as { detail?: string; title?: string };
    return p.detail ?? p.title;
  }
  return undefined;
}
